"use client";

/**
 * Office client tools as composable `defineClientTool` units.
 *
 * Each Office tool (Excel / Word / Outlook) is expressed as a single
 * `MagnusClientTool` that co-locates its host-side `execute` with its
 * generative UI `view`. This replaces the previous split between the
 * dispatch adapter (passed to `useCopilotTools`) and the parallel
 * view registry (consumed by `ToolRenderer`): both now derive from one
 * list passed to `<MagnusProvider tools={...}>`.
 *
 * The heavy per-host execution engine (Office.run, timeouts, sheet pinning,
 * batch sync strategy) stays in `lib/{excel,word,outlook}/tool-executors`;
 * each tool's `execute` just delegates to it by name.
 *
 * @see ADR-043 — Host-Neutral Client-Side Tool Protocol
 */

import { type AnyMagnusClientTool, defineClientTool } from "@bg-bgi/magnus-sdk";
import type { ToolUIMeta } from "@magnus/react";
import type { ClientToolExecutorAdapter } from "@magnus/react/copilot-tools";
import type { AppSurface } from "@workspace/services/domain";
import { excelToolRegistry } from "@/components/chat/excel/registry";
import { outlookToolRegistry } from "@/components/chat/outlook/registry";
// Side-effect import only: pulls the compile-time tool-view exhaustiveness
// guard into a module that is always part of the app's typecheck graph, so
// adding a server tool without a static view is a build error again (the guard
// was previously exported but never imported, so its `satisfies` never ran).
import "@/components/chat/tool-ui-exhaustiveness";
import { wordToolRegistry } from "@/components/chat/word/registry";
import { excelClientToolAdapter } from "@/lib/excel/tool-executors";
import { OUTLOOK_TOOL_NAMES } from "@/lib/outlook/tool-executors";
import { wordClientToolAdapter } from "@/lib/word/tool-executors";

function buildOfficeTools(
  adapter: ClientToolExecutorAdapter,
  registry: Partial<Record<string, ToolUIMeta>>
): AnyMagnusClientTool[] {
  return [...adapter.toolNames].map((name) => {
    const meta = registry[name];
    return defineClientTool({
      name,
      execute: (args) =>
        adapter.execute(name, (args ?? {}) as Record<string, unknown>),
      display: meta
        ? { kind: meta.kind, category: meta.category, view: meta.view }
        : undefined,
    });
  });
}

/**
 * A **view-only tool**: registered at the surface-level `MagnusProvider` so its
 * rich tool card resolves from the provider registry, but whose execution is
 * owned by a **chat-bound `ClientToolExecutorAdapter`** wired separately in
 * `chat-interface.tsx` via `useCopilotTools({ adapters })` (which takes
 * precedence over provider entries). Today only Outlook uses this split — its
 * tools touch the active chat's sandbox, so they can't be built at the
 * surface-level provider. The `execute` here is a guard that should never run;
 * if it does, the chat-bound adapter failed to match first. See ADR-043.
 */
function viewOnlyExecuteGuard(name: string): never {
  throw new Error(
    `"${name}" is a view-only tool — it must be executed by its chat-bound adapter`
  );
}

function buildViewOnlyTools(
  toolNames: ReadonlySet<string>,
  registry: Partial<Record<string, ToolUIMeta>>
): AnyMagnusClientTool[] {
  return [...toolNames].map((name) => {
    const meta = registry[name];
    return defineClientTool({
      name,
      execute: () => viewOnlyExecuteGuard(name),
      display: meta
        ? { kind: meta.kind, category: meta.category, view: meta.view }
        : undefined,
    });
  });
}

/**
 * Resolve the client tools for an Office host. Returns an empty list for
 * non-Office surfaces (the standalone web app registers no client tools).
 */
export function getOfficeClientTools(host: AppSurface): AnyMagnusClientTool[] {
  switch (host) {
    case "excel-add-in":
      return buildOfficeTools(excelClientToolAdapter, excelToolRegistry);
    case "word-add-in":
      return buildOfficeTools(wordClientToolAdapter, wordToolRegistry);
    case "outlook-add-in":
      return buildViewOnlyTools(OUTLOOK_TOOL_NAMES, outlookToolRegistry);
    default:
      // Standalone web and the Procore panel register no client tools —
      // Procore's capability is entirely server-side. Unlike the shell
      // registry, client-tool coverage is intentionally partial.
      return [];
  }
}
